import {
    ButtonGhost,
    ButtonSecondary,
    Colors,
    Icon,
    StepBadge,
    TextInput,
} from '@amelio/ui-library';
import { Controller, useFieldArray, type Control } from 'react-hook-form';

import { PLAN_FIELD_VARIANT } from './meetingPlanFieldVariant';
import { LIST_FIELD_LANE, LIST_FIELD_SIZE } from './meetingPlanListLayout';
import { MeetingPlanSectionCard } from './MeetingPlanSectionCard';
import {
    LIST_ENTRY_MAX_LENGTH,
    LIST_MAX_ENTRIES,
    type MeetingPlanTemplateFormData,
    type MeetingPlanTextListField,
} from './validation';

import { useAmelioTranslations } from '@/hooks';
import { validationMessageKey } from '@/utils/validationMessage';

type MeetingPlanTextListFieldsProps = {
    control: Control<MeetingPlanTemplateFormData>;
    name: MeetingPlanTextListField;
    heading: string;
    /** One line under the heading, saying what the list is for. */
    description?: string;
    entryLabel: string;
    addLabel: string;
    /** Numbers the entries, for a list a reader works through in order. */
    numbered?: boolean;
    dataTestId: string;
};

/**
 * One of the plan's plain lists — preparation questions, tips, what to observe. They differ only by
 * their heading and by whether their entries are numbered, so they are the same section three times
 * rather than three sections.
 *
 * Each entry is a single line with no visible label: the section's heading already says what every
 * line is, and a label repeated on each one made the page twice as long. The field is still named
 * for assistive technologies, with its position.
 */
export const MeetingPlanTextListFields = ({
    control,
    name,
    heading,
    description,
    entryLabel,
    addLabel,
    numbered = false,
    dataTestId,
}: MeetingPlanTextListFieldsProps) => {
    const { t } = useAmelioTranslations();
    const { fields, append, remove } = useFieldArray({ control, name });

    return (
        <MeetingPlanSectionCard
            title={heading}
            description={description}
            dataTestId={dataTestId}
            action={
                <ButtonSecondary
                    size={'sm'}
                    onClick={() => append({ text: '', storedText: null })}
                    disabled={fields.length >= LIST_MAX_ENTRIES}
                    fitContent
                    leftIcon={
                        <Icon
                            name={'essential-add'}
                            size={'sm'}
                            color={'inherit'}
                            ariaHidden
                        />
                    }
                    dataTestId={`${dataTestId}-add`}
                >
                    {addLabel}
                </ButtonSecondary>
            }
        >
            <ol className={'flex flex-col gap-3'}>
                {fields.map((field, index) => (
                    <li
                        key={field.id}
                        className={'flex items-start gap-3'}
                        data-testid={`${dataTestId}-${index}`}
                    >
                        {/* The list already numbers its entries for assistive technologies: the
                            badge is the visible number only. */}
                        {numbered && (
                            <div
                                aria-hidden={'true'}
                                className={LIST_FIELD_LANE}
                            >
                                <StepBadge
                                    step={index + 1}
                                    appearance={'ring'}
                                    size={'xs'}
                                />
                            </div>
                        )}

                        <Controller
                            control={control}
                            name={`${name}.${index}.text`}
                            render={({ field: entry, fieldState }) => (
                                <div className={'flex-1'}>
                                    <TextInput
                                        variant={PLAN_FIELD_VARIANT}
                                        value={entry.value}
                                        onChange={entry.onChange}
                                        maxLength={LIST_ENTRY_MAX_LENGTH}
                                        size={LIST_FIELD_SIZE}
                                        ariaLabel={`${entryLabel} ${index + 1}`}
                                        error={Boolean(fieldState.error)}
                                        errorMessage={
                                            fieldState.error?.message &&
                                            t(validationMessageKey(fieldState.error.message))
                                        }
                                        dataTestId={`${dataTestId}-${index}-text`}
                                    />
                                </div>
                            )}
                        />

                        <div className={LIST_FIELD_LANE}>
                            <ButtonGhost
                                size={'sm'}
                                onClick={() => remove(index)}
                                aria-label={t(
                                    'templateManagement.meetingPlanEditor.removeListEntry',
                                )}
                                dataTestId={`${dataTestId}-${index}-remove`}
                            >
                                <Icon
                                    name={'essential-trash'}
                                    size={'sm'}
                                    color={Colors.BASE_500}
                                    ariaHidden
                                />
                            </ButtonGhost>
                        </div>
                    </li>
                ))}
            </ol>
        </MeetingPlanSectionCard>
    );
};
